/**
 * Server function bridge for SSR tRPC calls.
 *
 * The `createServerFn` boundary is required by TanStack Start's import protection
 * so that `.server.ts` imports (appRouter, createTRPCContext) are tree-shaken from
 * the client bundle. On SSR the call stays in-process — no HTTP round-trip.
 *
 * A passthrough `validator` is used instead of Zod to type `data` without
 * serialization/deserialization overhead — the input comes from the typed tRPC
 * client (trusted in-process).
 */
import { createServerFn } from '@tanstack/react-start';
import { callTRPCProcedure } from '@trpc/server';
import { recordTrpcError, recordTrpcProcedure } from '@worldcoo/monolith/wide-events';
import { appRouter } from './trpc/router.server';
import { createTRPCContext } from './trpc/init.server';

type TRPCCallInput = { path: string; input: unknown; type: 'query' | 'mutation' };

export const trpcSSRCallFn = createServerFn({ method: 'POST' })
	.validator((input: TRPCCallInput) => input)
	.handler(async ({ data }) => {
		// `callTRPCProcedure` is the raw call — it has none of the adapter's responseMeta/onError hooks,
		// so the SSR path has to record the same fields by hand. Without this, only client-side tRPC
		// calls would carry `rpc.method` and every SSR-resolved procedure would be an anonymous request.
		//
		// Accumulating (not `recordTrpcResponse`) because one document render resolves MANY procedures
		// in sequence, all inside the same wide event — overwriting would leave only the last one.
		recordTrpcProcedure({ path: data.path, type: data.type });
		try {
			return await callTRPCProcedure({
				router: appRouter,
				path: data.path,
				getRawInput: () => Promise.resolve(data.input),
				ctx: createTRPCContext(),
				type: data.type,
				signal: undefined,
				batchIndex: 0,
			});
		} catch (error) {
			recordTrpcError({ error, path: data.path });
			throw error;
		}
	});
